import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Search, Send, MoreVertical, Sparkles, Clock, StickyNote, Zap,
  Phone, Mail, Building, User, ChevronRight, Loader2, CalendarIcon, X, MessageSquare,
  Paperclip, Image, FileText, Download, MessageSquareText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_COLORS = {
  nuevo: 'bg-blue-100 text-blue-800',
  contactado: 'bg-purple-100 text-purple-800',
  interesado: 'bg-yellow-100 text-yellow-800',
  propuesta: 'bg-emerald-100 text-emerald-800',
  cerrado: 'bg-green-100 text-green-800',
  perdido: 'bg-red-100 text-red-800'
};

const STAGE_NAMES = {
  nuevo: 'Nuevo',
  contactado: 'Contactado',
  interesado: 'Interesado',
  propuesta: 'Propuesta',
  cerrado: 'Cerrado',
  perdido: 'Perdido'
};

const Inbox = () => {
  const { token, user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contact, setContact] = useState(null);
  const [notes, setNotes] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [hsmTemplates, setHsmTemplates] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newNote, setNewNote] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState('12:00');
  const [uploading, setUploading] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const fetchConversations = async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/api/conversations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/conversations/${conversationId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const fetchContact = async (contactId) => {
    try {
      const response = await fetch(`${API_URL}/api/contacts/${contactId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContact(data);
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
    }
  };

  const fetchNotes = async (conversationId) => {
    try {
      const response = await fetch(`${API_URL}/api/conversations/${conversationId}/notes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const fetchQuickReplies = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quick-replies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQuickReplies(data);
      }
    } catch (error) {
      console.error('Error fetching quick replies:', error);
    }
  };

  const fetchHsmTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hsm-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHsmTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching HSM templates:', error);
    }
  };

  const sendHsmTemplate = async (template) => {
    if (!selectedConversation) return;
    
    setSendingTemplate(true);
    try {
      // For now, send without variables - in production you'd show a dialog to fill them
      const response = await fetch(`${API_URL}/api/hsm-templates/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          template_id: template.template_id,
          conversation_id: selectedConversation.conversation_id,
          variable_values: {}
        })
      });

      if (response.ok) {
        toast.success('Plantilla enviada');
        fetchMessages(selectedConversation.conversation_id);
        fetchConversations();
        setShowTemplateSelector(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al enviar plantilla');
      }
    } catch (error) {
      console.error('Error sending template:', error);
      toast.error('Error al enviar plantilla');
    } finally {
      setSendingTemplate(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchQuickReplies();
    fetchHsmTemplates();
  }, [token, searchQuery]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation.conversation_id);
      fetchContact(selectedConversation.contact_id);
      fetchNotes(selectedConversation.conversation_id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    setSendingMessage(true);
    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${selectedConversation.conversation_id}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content: newMessage })
        }
      );
      
      if (response.ok) {
        const msg = await response.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        fetchConversations();
        toast.success('Mensaje enviado');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Error al enviar mensaje');
    } finally {
      setSendingMessage(false);
    }
  };

  const scheduleMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !scheduleDate) return;
    
    const scheduledTime = new Date(scheduleDate);
    const [hours, minutes] = scheduleTime.split(':');
    scheduledTime.setHours(parseInt(hours), parseInt(minutes));
    
    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${selectedConversation.conversation_id}/schedule`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content: newMessage,
            scheduled_time: scheduledTime.toISOString()
          })
        }
      );
      
      if (response.ok) {
        setNewMessage('');
        setShowSchedule(false);
        setScheduleDate(null);
        toast.success('Mensaje programado exitosamente');
      }
    } catch (error) {
      console.error('Error scheduling message:', error);
      toast.error('Error al programar mensaje');
    }
  };

  const addNote = async () => {
    if (!newNote.trim() || !selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/conversations/${selectedConversation.conversation_id}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content: newNote })
        }
      );
      
      if (response.ok) {
        const note = await response.json();
        setNotes([note, ...notes]);
        setNewNote('');
        toast.success('Nota agregada');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Error al agregar nota');
    }
  };

  const suggestReply = async () => {
    if (!selectedConversation) return;
    
    setAiLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/ai/suggest-reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ conversation_id: selectedConversation.conversation_id })
      });
      
      if (response.ok) {
        const data = await response.json();
        setNewMessage(data.suggestion);
        toast.success('Sugerencia de IA generada');
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      toast.error('Error al obtener sugerencia');
    } finally {
      setAiLoading(false);
    }
  };

  const applyQuickReply = (content) => {
    setNewMessage(content);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Solo imágenes (JPG, PNG, WEBP) y PDF.');
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Archivo demasiado grande. Máximo 10MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.detail || 'Error al subir archivo');
      }

      const uploadResult = await uploadResponse.json();

      // Send media message
      const messageResponse = await fetch(
        `${API_URL}/api/conversations/${selectedConversation.conversation_id}/messages/media`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            content: newMessage || null,
            media_url: uploadResult.file_url,
            media_type: uploadResult.media_type,
            file_name: uploadResult.original_name
          })
        }
      );

      if (messageResponse.ok) {
        const msg = await messageResponse.json();
        setMessages([...messages, msg]);
        setNewMessage('');
        fetchConversations();
        toast.success('Archivo enviado');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error al enviar archivo');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper to render message content with media
  const renderMessageContent = (msg) => {
    const isImage = msg.message_type === 'image';
    const isDocument = msg.message_type === 'document';
    
    if (isImage && msg.media_url) {
      return (
        <div className="space-y-2">
          <img 
            src={msg.media_url} 
            alt="Imagen adjunta" 
            className="max-w-[250px] rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(msg.media_url, '_blank')}
          />
          {msg.content && !msg.content.startsWith('📷') && (
            <p className="text-slate-800">{msg.content}</p>
          )}
        </div>
      );
    }
    
    if (isDocument && msg.media_url) {
      return (
        <div className="space-y-2">
          <a 
            href={msg.media_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 p-2 bg-white/50 rounded-lg hover:bg-white/80 transition-colors"
          >
            <FileText className="w-8 h-8 text-red-500" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-700 truncate">
                {msg.file_name || 'Documento'}
              </p>
              <p className="text-xs text-slate-500">PDF</p>
            </div>
            <Download className="w-4 h-4 text-slate-500" />
          </a>
          {msg.content && !msg.content.startsWith('📎') && (
            <p className="text-slate-800">{msg.content}</p>
          )}
        </div>
      );
    }
    
    return <p className="text-slate-800">{msg.content}</p>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 text-[#25D366] animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-0px)] overflow-hidden bg-white" data-testid="inbox-container">
      {/* Conversations List */}
      <div className="w-80 lg:w-96 border-r border-slate-200 flex flex-col bg-slate-50">
        <div className="p-4 border-b border-slate-200">
          <h2 className="text-xl font-bold font-['Outfit'] text-slate-900 mb-4">Bandeja de entrada</h2>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar conversaciones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-conversations"
              className="pl-10 bg-white border-slate-200"
            />
          </div>
        </div>
        
        <ScrollArea className="flex-1">
          <div className="p-2">
            {conversations.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>No hay conversaciones</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <div
                  key={conv.conversation_id}
                  data-testid={`conversation-${conv.conversation_id}`}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-3 rounded-lg cursor-pointer transition-colors mb-1 ${
                    selectedConversation?.conversation_id === conv.conversation_id
                      ? 'bg-white shadow-sm ring-1 ring-slate-200'
                      : 'hover:bg-slate-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="h-10 w-10 flex-shrink-0">
                      <AvatarFallback className="bg-[#25D366] text-white text-sm">
                        {conv.contact_name?.charAt(0) || 'C'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-900 truncate">{conv.contact_name}</span>
                        <span className="text-xs text-slate-500">
                          {conv.last_message_time && format(new Date(conv.last_message_time), 'HH:mm')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 truncate">{conv.last_message || 'Sin mensajes'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className={`text-xs ${STAGE_COLORS[conv.stage]}`}>
                          {STAGE_NAMES[conv.stage]}
                        </Badge>
                        {conv.unread_count > 0 && (
                          <span className="bg-[#25D366] text-white text-xs px-2 py-0.5 rounded-full">
                            {conv.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="h-16 px-4 flex items-center justify-between border-b border-slate-200 bg-white">
              <div className="flex items-center gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarFallback className="bg-[#25D366] text-white">
                    {selectedConversation.contact_name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedConversation.contact_name}</h3>
                  <p className="text-sm text-slate-500">{selectedConversation.contact_phone}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <Badge className={STAGE_COLORS[selectedConversation.stage]}>
                  {STAGE_NAMES[selectedConversation.stage]}
                </Badge>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 chat-pattern p-4">
              <div className="max-w-3xl mx-auto space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.sender_type === 'agent' || msg.sender_type === 'bot' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-2 shadow-sm ${
                        msg.sender_type === 'agent' || msg.sender_type === 'bot'
                          ? 'message-bubble-agent'
                          : 'message-bubble-contact'
                      }`}
                    >
                      {msg.sender_type === 'bot' && (
                        <div className="flex items-center gap-1 mb-1 text-xs text-purple-600">
                          <span className="text-sm">🤖</span> Bot
                        </div>
                      )}
                      {renderMessageContent(msg)}
                      <p className="text-xs text-slate-500 mt-1 text-right">
                        {format(new Date(msg.created_at), 'HH:mm')}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input Area */}
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex items-center gap-2 mb-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={suggestReply}
                  disabled={aiLoading}
                  data-testid="ai-suggest-btn"
                  className="border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10"
                >
                  {aiLoading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  Sugerir con IA
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="quick-replies-btn">
                      <Zap className="w-4 h-4 mr-1" />
                      Respuestas rápidas
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64">
                    {quickReplies.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500 text-center">
                        Sin respuestas rápidas
                      </div>
                    ) : (
                      quickReplies.map((qr) => (
                        <DropdownMenuItem
                          key={qr.quick_reply_id}
                          onClick={() => applyQuickReply(qr.content)}
                          className="flex flex-col items-start"
                        >
                          <span className="font-medium">{qr.title}</span>
                          <span className="text-xs text-slate-500 truncate w-full">{qr.content}</span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Popover open={showSchedule} onOpenChange={setShowSchedule}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="schedule-btn">
                      <Clock className="w-4 h-4 mr-1" />
                      Programar
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-4" align="start">
                    <div className="space-y-4">
                      <h4 className="font-medium">Programar mensaje</h4>
                      <Calendar
                        mode="single"
                        selected={scheduleDate}
                        onSelect={setScheduleDate}
                        locale={es}
                        className="rounded-md border"
                      />
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-32"
                        />
                        <Button
                          size="sm"
                          onClick={scheduleMessage}
                          disabled={!scheduleDate || !newMessage.trim()}
                          className="bg-[#25D366] hover:bg-[#1DA851]"
                        >
                          Programar
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="notes-btn">
                      <StickyNote className="w-4 h-4 mr-1" />
                      Notas ({notes.length})
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Notas internas</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="flex gap-2">
                        <Textarea
                          placeholder="Agregar nota..."
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          className="min-h-[80px]"
                        />
                      </div>
                      <Button 
                        onClick={addNote} 
                        disabled={!newNote.trim()}
                        className="w-full bg-[#25D366] hover:bg-[#1DA851]"
                      >
                        Agregar nota
                      </Button>
                      <ScrollArea className="h-[200px]">
                        {notes.map((note) => (
                          <div key={note.note_id} className="p-3 bg-yellow-50 rounded-lg mb-2 border border-yellow-100">
                            <p className="text-sm text-slate-700">{note.content}</p>
                            <p className="text-xs text-slate-500 mt-2">
                              {note.created_by_name} - {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                            </p>
                          </div>
                        ))}
                      </ScrollArea>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".jpg,.jpeg,.png,.webp,.pdf"
                className="hidden"
              />
              
              <div className="flex items-center gap-2">
                {/* Attach file button */}
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading || !selectedConversation}
                  data-testid="attach-file-btn"
                  className="h-[44px] w-[44px] border-slate-200"
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Paperclip className="w-5 h-5 text-slate-500" />
                  )}
                </Button>

                {/* HSM Templates Button */}
                <Popover open={showTemplateSelector} onOpenChange={setShowTemplateSelector}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={!selectedConversation || hsmTemplates.length === 0}
                      data-testid="hsm-templates-btn"
                      className="h-[44px] w-[44px] border-slate-200"
                    >
                      <MessageSquareText className="w-5 h-5 text-purple-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <div className="p-3 border-b bg-slate-50">
                      <h4 className="font-medium text-sm">Plantillas HSM</h4>
                      <p className="text-xs text-slate-500">Selecciona una plantilla para enviar</p>
                    </div>
                    <ScrollArea className="max-h-[300px]">
                      {hsmTemplates.map((template) => (
                        <button
                          key={template.template_id}
                          onClick={() => sendHsmTemplate(template)}
                          disabled={sendingTemplate}
                          className="w-full p-3 text-left hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium text-sm text-slate-700">{template.name}</span>
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded">
                              {template.category}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-2">{template.content}</p>
                        </button>
                      ))}
                      {hsmTemplates.length === 0 && (
                        <div className="p-4 text-center text-sm text-slate-400">
                          No hay plantillas disponibles
                        </div>
                      )}
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
                
                <Textarea
                  placeholder="Escribe un mensaje..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  data-testid="message-input"
                  className="min-h-[44px] max-h-[120px] resize-none"
                />
                <Button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  data-testid="send-message-btn"
                  className="h-[44px] px-6 bg-[#25D366] hover:bg-[#1DA851]"
                >
                  {sendingMessage ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              
              {/* File type hint */}
              <p className="text-xs text-slate-400 mt-1">
                📎 Archivos permitidos: JPG, PNG, WEBP, PDF (máx. 10MB)
              </p>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg">Selecciona una conversación</p>
              <p className="text-sm">para ver los mensajes</p>
            </div>
          </div>
        )}
      </div>

      {/* Contact Details Sidebar */}
      {selectedConversation && contact && (
        <div className="hidden xl:block w-80 border-l border-slate-200 bg-white p-6 overflow-y-auto">
          <h3 className="font-semibold text-lg mb-4 font-['Outfit']">Detalles del contacto</h3>
          
          <div className="flex flex-col items-center mb-6">
            <Avatar className="h-20 w-20 mb-3">
              <AvatarFallback className="bg-[#25D366] text-white text-2xl">
                {contact.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <h4 className="font-semibold text-slate-900">{contact.name}</h4>
            <Badge className={`mt-2 ${STAGE_COLORS[contact.stage]}`}>
              {STAGE_NAMES[contact.stage]}
            </Badge>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-sm">
              <Phone className="w-4 h-4 text-slate-400" />
              <span className="text-slate-700">{contact.phone}</span>
            </div>
            {contact.email && (
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">{contact.email}</span>
              </div>
            )}
            {contact.company && (
              <div className="flex items-center gap-3 text-sm">
                <Building className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">{contact.company}</span>
              </div>
            )}
            {contact.assigned_agent_name && (
              <div className="flex items-center gap-3 text-sm">
                <User className="w-4 h-4 text-slate-400" />
                <span className="text-slate-700">Asignado a: {contact.assigned_agent_name}</span>
              </div>
            )}
          </div>

          {contact.notes && (
            <div className="mt-6">
              <h4 className="font-medium text-slate-900 mb-2">Notas</h4>
              <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{contact.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Inbox;
